/**
 * Данные разбора хозяйства по типу проблемы (`ReviewByKind`, `ProblemCard`).
 *
 * Запрашивается только то, что нужно шаблону:
 *   - всегда: разбор хозяйства по типу сигнала сервера
 *     (`season/farm-breakdown?actor_id`, 6al.2: сроки / вода по поливам /
 *     расхождения / карантин — один вызов, общий для всех карточек хозяйства);
 *     картина сезона хозяйства (`season/picture?actor_id&summary`) — ссылки на
 *     работы (кампании) и выбор работы на карте; земля хозяйства
 *     (`/actors/{id}`) — рамка карты;
 *   - карта Сроков / Воды: статус работы по контурам
 *     (`season/contour-status?actor_id&work_def_id`) — сервер, не догадка;
 *   - Вода / Совпадение: распределение выбранного полива по контурам
 *     (`water/works/{id}`).
 * Старый сервер без разбора (404/ошибка) — прежняя клиентская сборка: водная
 * сводка по поливам, очередь проверки, статусы карантина × земля.
 */
import { useMemo, useState } from 'react';
import { api, type QueryParams, type Schemas } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { toFarmLand } from '../../data-source/api/cards';
import { once } from '../../data-source/api/dataset';
import { workStatusByContour } from '../../map/layers';
import { countOf, WORDS } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';
import type { WorkStatusValue } from '../../map/paint';
import type { ContourMeta, QuarantineStatusValue } from '../../data/types';
import {
  breakdownConflicts,
  breakdownIrrigation,
  breakdownOverdue,
  breakdownQuarantine,
  breakdownSoon,
  closingSoon,
  conflictContours,
  conflictLayer,
  conflictRows,
  irrigationRows,
  pickIrrigation,
  quarantineRows,
  toOverdueWork,
  topOverdue,
  waterByContour,
  waterEventLayer,
  deadlineLayer,
  type ConflictRow,
  type FarmBreakdown,
  type IrrigationRow,
  type OverdueWork,
  type ProblemKind,
  type QuarantineRow,
  type WaterState,
} from './model';
import type { ProblemLayer, ProblemMapData } from './ProblemMap';

type Picture = Schemas['SeasonPictureOut'];

const msg = defineMessages({
  mapWork: { id: 'board.reviewMap.work', defaultMessage: 'Работа на карте: {work}', description: 'Подпись под картой разбора «Сроки»; {work} — название выбранной работы' },
  mapNoOverdue: { id: 'board.reviewMap.noOverdue', defaultMessage: 'Работ с просрочкой нет', description: 'Подпись под картой разбора «Сроки», когда просроченных работ нет' },
  mapIrrigation: { id: 'board.reviewMap.irrigation', defaultMessage: '{work}: полив по контурам по файлу ирригации', description: 'Подпись под картой разбора «Вода»; {work} — название выбранного полива' },
  mapNoIrrigation: { id: 'board.reviewMap.noIrrigation', defaultMessage: 'Поливов в срезе нет', description: 'Подпись под картой разбора «Вода», когда поливов нет' },
  mapConflicts: { id: 'board.reviewMap.conflicts', defaultMessage: 'Контуры с расхождением: {count}', description: 'Подпись под картой разбора «Расхождения»; {count} — число контуров' },
  mapConflictsNone: { id: 'board.reviewMap.conflictsNone', defaultMessage: 'Контуры с расхождением сервер не назвал — показана земля хозяйства', description: 'Подпись под картой разбора «Расхождения», когда спорных контуров сервер не назвал' },
  mapQuarantine: { id: 'board.reviewMap.quarantine', defaultMessage: 'Карантин: {contours} с сигналом', description: 'Подпись под картой разбора «Карантин»; {contours} — число контуров словом («3 контура»)' },
  mapQuarantineLand: { id: 'board.reviewMap.quarantineLand', defaultMessage: 'Карантин по земле хозяйства', description: 'Подпись под картой разбора «Карантин», когда сигнальных контуров нет' },
  noAnswer: { id: 'board.reviewMap.noAnswer', defaultMessage: 'Сервер не ответил.', description: 'Текст ошибки загрузки разбора, когда у ошибки нет собственного текста' },
});

/** Срез разбора: общий `as_of`/демо, цикл и ссылочный хвост. */
export interface ProblemScope {
  /** `ctx.params` контекста анализа: `as_of`, `include_demo`. */
  params: QueryParams;
  /**
   * Канонический ключ цикла: «2026:COTTON».
   */
  cycle: string;
  /** Дата состояния (уже разрешённая: `as_of` или горизонт данных). */
  asOf: string;
  /** Хвост ссылки с общим срезом (`ctx.linkSearch`). */
  linkSearch: (extra?: Record<string, string | number>) => string;
}

export interface FarmReview {
  loading: boolean;
  /** Ошибка загрузки разбора (картина или земля хозяйства) — не «данных нет». */
  error: string | null;
  /** Повторить запросы разбора после ошибки связи. */
  reload: () => void;
  /** Разбор сервера; `null` — ещё грузится или старый сервер (клиентская сборка). */
  breakdown: FarmBreakdown | null;
  picture: Picture | null;
  land: ContourMeta[] | null;
  /** Все работы цикла хозяйства (для выбора «Работа на карте»). */
  works: OverdueWork[];
  overdue: OverdueWork[];
  soon: OverdueWork[];
  irrigation: IrrigationRow[] | null;
  irrigationId: number | null;
  setIrrigationId: (id: number) => void;
  mapWorkId: number | null;
  setMapWorkId: (id: number) => void;
  conflicts: ConflictRow[] | null;
  quarantine: QuarantineRow[] | null;
  map: { layer: ProblemLayer; data: ProblemMapData; filterIds: string[] | undefined; focusIds: string[] | undefined; caption: string };
}

const needsWater = (k: ProblemKind) => k === 'water' || k === 'overlap';

const Q_VALUE: Record<string, QuarantineStatusValue> = { red: 'red', yellow: 'watch', green: 'green' };

/** Разбор хозяйства: один вызов на срез, общий для всех карточек страницы. */
export function loadFarmBreakdown(params: QueryParams, cycle: string, farmId: number): Promise<FarmBreakdown> {
  const query = { ...params, cycle, actor_id: farmId };
  return once(`farm-breakdown:${JSON.stringify(query)}`, () => api.seasonFarmBreakdown(query));
}

export function useFarmReview(kind: ProblemKind, farmId: number | null, scope: ProblemScope): FarmReview {
  const base = scope.params;
  const key = `${JSON.stringify(base)}|${scope.cycle}|${farmId ?? ''}`;

  const picture = useRequest<Picture | null>(
    () => (farmId ? api.seasonPicture({ ...base, cycle: scope.cycle, actor_id: farmId, summary: true, weeks: 1 }) : Promise.resolve(null)),
    [key],
  );
  const breakdownReq = useRequest<FarmBreakdown | null>(
    () => (farmId ? loadFarmBreakdown(base, scope.cycle, farmId) : Promise.resolve(null)),
    [key],
  );
  const breakdown = breakdownReq.data ?? null;
  /** Старый сервер: разбора нет — данные собираются по-прежнему на клиенте. */
  const legacy = breakdownReq.state === 'error';
  const land = useRequest<ContourMeta[] | null>(
    async () => (farmId ? toFarmLand(await api.actor(farmId, base)) : null),
    [key],
  );
  const asOf = scope.asOf;
  const allWorks = useMemo(() => picture.data?.summary?.works ?? [], [picture.data]);
  const irrigationWorks = useMemo(() => allWorks.filter(w => w.category === 'irrigation' && !!w.window_from && w.window_from <= asOf), [allWorks, asOf]);
  const irrigationKey = irrigationWorks.map(w => w.work_def_id).join(',');

  const water = useRequest<Map<number, { requested: number | null; delivered: number | null; rows?: number }> | null>(
    async () => {
      if (!farmId || !legacy || !needsWater(kind) || !irrigationWorks.length) return null;
      const pairs = await Promise.all(irrigationWorks.map(async w => {
        try {
          const r = await api.waterSummary('actor', { ...base, actor_id: farmId, work_def_id: w.work_def_id });
          const row = r.rows[0];
          const n = (v: string | null | undefined) => (v === null || v === undefined ? null : Number(v));
          return [w.work_def_id, { requested: row ? n(row.requested_m3) : null, delivered: row ? n(row.delivered_m3) : null, rows: r.rows.length }] as const;
        } catch {
          return [w.work_def_id, { requested: null, delivered: null } as { requested: number | null; delivered: number | null; rows?: number }] as const;
        }
      }));
      return new Map(pairs);
    },
    [key, kind, irrigationKey, legacy],
  );
  const irrigation = useMemo(() => {
    if (!needsWater(kind) || !picture.data) return null;
    if (breakdown) return breakdownIrrigation(breakdown.water, allWorks, asOf);
    return legacy ? irrigationRows(allWorks, water.data ?? new Map(), asOf) : null;
  }, [kind, picture.data, breakdown, legacy, allWorks, water.data, asOf]);
  const [pickedIrrigation, setIrrigationId] = useState<number | null>(null);
  const selectedIrrigation = irrigation?.find(r => r.workDefId === pickedIrrigation) ?? (irrigation ? pickIrrigation(irrigation) : null);
  // Подача по контурам — только для старой цепочки «заявка → подача» (сервер
  // без разбора). Вода по событиям файла ирригации красится статусом полива
  // по контурам с сервера (agroai-0e8): распределений у событий нет.
  const allocations = useRequest<Record<string, WaterState> | null>(
    async () => (farmId && legacy && needsWater(kind) && selectedIrrigation ? waterByContour((await api.waterWork(selectedIrrigation.workDefId, base)).allocations, farmId) : null),
    [key, kind, legacy, selectedIrrigation?.workDefId ?? null],
  );

  const queue = useRequest(
    () => (farmId && legacy && kind === 'conflicts' ? api.verificationQueue({ ...base, actor_id: farmId, limit: 200, only_awaiting: false }) : Promise.resolve(null)),
    [key, kind, legacy],
  );
  const statuses = useRequest(
    () => (farmId && legacy && kind === 'quarantine' ? api.quarantineStatuses({ ...base, limit: 500 }) : Promise.resolve(null)),
    [key, kind, legacy],
  );

  const works = useMemo(() => allWorks.map(w => toOverdueWork(w, asOf)), [allWorks, asOf]);
  const overdue = useMemo(
    () => (breakdown ? breakdownOverdue(breakdown.deadlines, allWorks, asOf, 3) : topOverdue(allWorks, asOf, 3)),
    [breakdown, allWorks, asOf],
  );
  const soon = useMemo(
    () => (breakdown ? breakdownSoon(breakdown.deadlines, allWorks, asOf) : closingSoon(allWorks, asOf, 5)),
    [breakdown, allWorks, asOf],
  );
  const cycleCampaigns = useMemo(() => new Set(allWorks.map(w => w.campaign_id)), [allWorks]);
  const conflicts = useMemo(() => {
    if (breakdown) return breakdownConflicts(breakdown.conflicts);
    return queue.data ? conflictRows(queue.data.items.filter(i => cycleCampaigns.has(i.campaign_id))) : null;
  }, [breakdown, queue.data, cycleCampaigns]);
  const landIds = useMemo(() => (land.data ? land.data.map(c => c.id) : null), [land.data]);
  const quarantine = useMemo(() => {
    if (breakdown) return breakdownQuarantine(breakdown.quarantine);
    return statuses.data && landIds ? quarantineRows(statuses.data.items, new Set(landIds), asOf) : null;
  }, [breakdown, statuses.data, landIds, asOf]);

  // «Работа на карте» для Сроков: по умолчанию — с наибольшей просрочкой.
  const [pickedWork, setMapWorkId] = useState<number | null>(null);
  const mapWork = works.find(w => w.workDefId === pickedWork) ?? overdue[0] ?? soon[0] ?? null;

  /**
   * Статус работы по контурам хозяйства — с сервера (`season/contour-status`):
   * Сроки — выбранная работа, Вода/Совпадение — выбранный полив.
   */
  const statusWorkId =
    kind === 'deadlines' ? mapWork?.workDefId ?? null : needsWater(kind) ? selectedIrrigation?.workDefId ?? null : null;
  const contourStatus = useRequest(
    () =>
      farmId && statusWorkId !== null && !legacy
        ? api.seasonContourStatus({ ...base, cycle: scope.cycle, actor_id: farmId, work_def_id: statusWorkId })
        : Promise.resolve(null),
    [key, statusWorkId, legacy],
  );
  const serverStatus = useMemo(
    () => (contourStatus.data ? workStatusByContour(contourStatus.data.items) : null),
    [contourStatus.data],
  );

  const map = useMemo((): FarmReview['map'] => {
    const filterIds = landIds ?? undefined;
    const contourRows = picture.data?.summary?.by_contour ?? [];
    /** Статус работы по контурам: точно, когда у работы ровно по обязательству на контур. */
    const statusFor = (w: OverdueWork | null): Record<string, WorkStatusValue> => {
      if (!w) return {};
      if (serverStatus && contourStatus.data?.work_def_id === w.workDefId) return serverStatus;
      const open = w.windowFrom !== null && w.windowFrom <= asOf && (w.windowTo ?? '') >= asOf;
      const ahead = w.windowFrom !== null && w.windowFrom > asOf;
      const silent: WorkStatusValue = ahead ? 'upcoming' : open ? 'in_window' : 'silent';
      const out: Record<string, WorkStatusValue> = {};
      if (w.obligations === contourRows.length) for (const r of contourRows) out[String(r.id)] = ahead ? 'upcoming' : 'reported';
      for (const id of w.silentContours) out[id] = silent;
      return out;
    };
    switch (kind) {
      case 'deadlines': {
        // Только проблемные контуры выбранной работы (после срока / окно закрывается).
        const closing = !!mapWork && mapWork.windowFrom !== null && mapWork.windowFrom <= asOf && (mapWork.windowTo ?? '') >= asOf;
        const layer = deadlineLayer(statusFor(mapWork), closing);
        const hot = Object.keys(layer.byId);
        return {
          layer: 'deadlines',
          data: { values: layer },
          filterIds,
          focusIds: hot.length ? hot : filterIds,
          caption: mapWork ? tr(msg.mapWork, { work: mapWork.name }) : tr(msg.mapNoOverdue),
        };
      }
      case 'water':
      case 'overlap': {
        const w = selectedIrrigation ? works.find(x => x.workDefId === selectedIrrigation.workDefId) ?? null : null;
        if (!legacy) {
          // Полито / без подачи по событиям файла: статус полива по контурам.
          const layer = waterEventLayer(statusFor(w));
          const dry = Object.entries(layer.byId).filter(([, v]) => v === 'no_delivery').map(([id]) => id);
          return {
            layer: 'waterEvents',
            data: { values: layer },
            filterIds,
            focusIds: dry.length ? dry : filterIds,
            caption: selectedIrrigation ? tr(msg.mapIrrigation, { work: selectedIrrigation.name }) : tr(msg.mapNoIrrigation),
          };
        }
        return {
          layer: 'water',
          data: { waterById: allocations.data ?? undefined, workStatusById: statusFor(w) },
          filterIds,
          focusIds: filterIds,
          caption: selectedIrrigation ? tr(msg.mapIrrigation, { work: selectedIrrigation.name }) : tr(msg.mapNoIrrigation),
        };
      }
      case 'conflicts': {
        const fromBreakdown = (conflicts ?? []).map(r => r.contourId).filter((id): id is string => !!id);
        const ids = fromBreakdown.length ? [...new Set(fromBreakdown)] : conflictContours(contourRows);
        // Земля хозяйства целиком, спорные — цветом проблемы, кадр — на них
        // (agroai-6al.15). Контур спора вне земли хозяйства в кадр тоже
        // попадает: сервер назвал его, и молча выбросить его нельзя.
        const land = filterIds ?? [];
        const shown = ids.length ? [...new Set([...land, ...ids])] : filterIds;
        return {
          layer: ids.length ? 'conflicts' : 'crop',
          data: ids.length ? { values: conflictLayer(ids) } : {},
          filterIds: shown,
          focusIds: ids.length ? ids : filterIds,
          caption: ids.length ? tr(msg.mapConflicts, { count: ids.length }) : tr(msg.mapConflictsNone),
        };
      }
      case 'quarantine': {
        const byId: Record<string, QuarantineStatusValue> = {};
        for (const id of landIds ?? []) byId[id] = 'green';
        for (const r of quarantine ?? []) byId[r.contourId] = Q_VALUE[r.status] ?? 'green';
        const hot = (quarantine ?? []).map(r => r.contourId);
        return {
          layer: 'quarantine',
          data: { quarantineById: byId },
          filterIds,
          focusIds: hot.length ? hot : filterIds,
          caption: hot.length ? tr(msg.mapQuarantine, { contours: countOf(hot.length, WORDS.contour) }) : tr(msg.mapQuarantineLand),
        };
      }
    }
  }, [kind, landIds, picture.data, mapWork, selectedIrrigation, works, allocations.data, quarantine, conflicts, asOf, serverStatus, contourStatus.data, legacy]);

  const reqs = [
    picture,
    land,
    breakdownReq,
    ...(legacy && needsWater(kind) ? [water] : []),
    ...(legacy && kind === 'conflicts' ? [queue] : []),
    ...(legacy && kind === 'quarantine' ? [statuses] : []),
  ];
  return {
    loading: reqs.some(r => r.state === 'loading'),
    error: picture.state === 'error' && !picture.data
      ? picture.error?.message ?? tr(msg.noAnswer)
      : land.state === 'error' && !land.data
        ? land.error?.message ?? tr(msg.noAnswer)
        : null,
    reload: () => { picture.reload(); land.reload(); breakdownReq.reload(); },
    breakdown,
    picture: picture.data ?? null,
    land: land.data ?? null,
    works,
    overdue,
    soon,
    irrigation,
    irrigationId: selectedIrrigation?.workDefId ?? null,
    setIrrigationId,
    mapWorkId: mapWork?.workDefId ?? null,
    setMapWorkId,
    conflicts,
    quarantine,
    map,
  };
}

/**
 * Цикл для ссылки на работу: свой цикл работы (картина по нескольким циклам
 * называет его в строке), иначе цикл среза — если он один. Список циклов без
 * своего цикла у работы — ссылки нет: вести в чужой цикл нельзя.
 */
export function workCycle(scope: Pick<ProblemScope, 'cycle'>, own?: string | null): string | null {
  if (own) return own;
  return scope.cycle.includes(',') ? null : scope.cycle;
}
